import type { TaskData } from "./TaskRow";
import type { ListOption } from "./DetailsPanel";
import ContextMenu from "./ContextMenu";
import { addDaysIso, isWeekend, nextWeekStartIso, nextWeekendStartIso, todayIso, weekendStartIso } from "@/lib/dates";
import { taskCount } from "@/lib/plural";

/** Everything the menu can apply to the selection in one request. */
// A type alias, not an interface: only aliases carry an implicit index signature, and
// updateTask() takes a Record<string, FormDataConvertible>.
export type MenuPatch = {
  my_day_on?: string | null;
  due_on?: string | null;
  list_id?: string;
};

interface Props {
  /** The effective selection the menu acts on — one task, or many when multi-selected. */
  tasks: TaskData[];
  /** The buckets the selection can be moved into. Omit to hide the move items. */
  lists?: ListOption[];
  x: number;
  y: number;
  onClose: () => void;
  onPatch: (payload: MenuPatch) => void;
  onDelete: () => void;
  /**
   * Moves the single task to the end of the other My Day block — "По порядку" (true) or
   * "В любое время" (false). Passed by My Day only, for a top-level task on today's board.
   */
  onMoveToMyDayBlock?: (sequential: boolean) => void;
}

export default function TaskContextMenu({
  tasks,
  lists = [],
  x,
  y,
  onClose,
  onPatch,
  onDelete,
  onMoveToMyDayBlock,
}: Props) {
  const today = todayIso();
  const count = tasks.length;
  const multi = count > 1;
  const allInMyDay = count > 0 && tasks.every((t) => t.myDayOn === today);

  // Subtasks are movable too — naming a bucket for one promotes it to a task of its own
  // there (see Task#move_to_list!), like dragging a subtask onto a bucket in the sidebar.
  const movable = count > 0 && lists.length > 0;
  const alreadyIn = (bucketId: string) => tasks.every((t) => (t.parentId ?? null) === null && t.listId === bucketId);

  function act(payload: Parameters<Props["onPatch"]>[0]) {
    onPatch(payload);
    onClose();
  }

  return (
    <ContextMenu x={x} y={y} onClose={onClose} testId="task-context-menu">
      {multi && (
        <div className="px-2 py-1 text-xs font-medium text-gray-400 dark:text-gray-500 select-none">
          {taskCount(count)}
        </div>
      )}
      {multi ? (
        <>
          <button
            type="button"
            onClick={() => act({ my_day_on: today })}
            className="details-popover-item"
          >
            В «Мой день»
          </button>
          <button
            type="button"
            onClick={() => act({ my_day_on: null })}
            className="details-popover-item"
          >
            Убрать из «Мой день»
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={() => act({ my_day_on: allInMyDay ? null : today })}
          className="details-popover-item"
        >
          {allInMyDay ? "Убрать из «Мой день»" : "В «Мой день»"}
        </button>
      )}
      {!multi && onMoveToMyDayBlock && (
        <button
          type="button"
          onClick={() => {
            onMoveToMyDayBlock(!tasks[0].myDaySequential);
            onClose();
          }}
          className="details-popover-item"
        >
          {tasks[0].myDaySequential ? "В любое время" : "По порядку"}
        </button>
      )}
      <button type="button" onClick={() => act({ due_on: today })} className="details-popover-item">
        Сегодня
      </button>
      <button
        type="button"
        onClick={() => act({ due_on: addDaysIso(today, 1) })}
        className="details-popover-item"
      >
        Завтра
      </button>
      {isWeekend() ? (
        <button
          type="button"
          onClick={() => act({ due_on: nextWeekendStartIso() })}
          className="details-popover-item"
        >
          Следующие выходные
        </button>
      ) : (
        <button
          type="button"
          onClick={() => act({ due_on: weekendStartIso() })}
          className="details-popover-item"
        >
          Выходные
        </button>
      )}
      <button
        type="button"
        onClick={() => act({ due_on: nextWeekStartIso() })}
        className="details-popover-item"
      >
        Следующая неделя
      </button>
      {movable &&
        lists.filter((bucket) => !alreadyIn(bucket.id)).map((bucket) => (
          <button key={bucket.id} type="button" onClick={() => act({ list_id: bucket.id })} className="details-popover-item">
            {bucket.kind === "inbox" ? "Во Входящее" : "В Задачи"}
          </button>
        ))}
      <button
        type="button"
        onClick={() => {
          onDelete();
          onClose();
        }}
        className="details-popover-item text-red-600 dark:text-red-400"
      >
        {multi ? `Удалить ${taskCount(count)}` : "Удалить"}
      </button>
    </ContextMenu>
  );
}
